import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useAddGoalByProgramId,
  useGetGoalDetailsById,
  useUpdateGoalById,
} from "../../../../../../../../../../api/services/program/programApis";
import {
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiGrid,
  MuiModal,
  MuiSelect,
  MuiTextField,
  STATUS_BADGE_TYPE,
  StatusBadgeType,
  WarningConfirmationModal,
} from "../../../../../../../../../../components";
import {
  MAX_LENGTH_256,
  MAX_LENGTH_500,
  MAX_LENGTH_75,
} from "../../../../../../../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../../../../../../../helpers/constants/componentIds";
import { getDateFormatByCountry } from "../../../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  convertToUTCFormat,
  getFieldUndefinedForPayloadParams,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
  isStatusActive,
  replaceHashWithValue,
} from "../../../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../../../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../../../../../redux/slices/appState/appStateSlice";
import { programStatusSelector } from "../../../../../../../../../../redux/slices/program/programSelector";
import { pageId } from "../../../../../../../../../createUser/CreateUser";
import { ParticipantDetail } from "../../../../../../../../modelsAndSchemas/Models";
import {
  AddGoalFormKey,
  AddGoalParams,
  UpdateGoalParams,
} from "../../modelsAndSchemas/Models";
import { AddGoalSchema } from "../../modelsAndSchemas/ValidationSchema";
import { addGoalSelectionSiteCoreKeys } from "../../SiteCoreKeys";
import { AddGoalWrapper } from "./AddGoal.style";

interface AddGoalProps {
  open: boolean;
  onClose: () => void;
  programId: string;
  onGoalAdded: (programId: string) => void;
  participantDetail?: ParticipantDetail;
  isEdit?: boolean;
  goalId?: string;
}

export const AddGoal: FC<AddGoalProps> = ({
  open,
  onClose,
  programId,
  onGoalAdded,
  participantDetail,
  isEdit = false,
  goalId,
}) => {
  const {
    useFetchLabel,
    commonLabels,
    fetchComponentTitle,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { isMobile } = useMediaQueries();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { gridSizeMedium: gridSize } = useGridContainer(true);
  const { commonARIALabel } = useNavigationSiteCore();
  const programStatusId = useSelector(programStatusSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const [showCancel, setShowCancel] = useState(false);
  const [isCompletionDateEnabled, setIsCompletionDateEnabled] = useState(false);

  useEffect(() => {
    if (
      isNotEmpty(participantDetail?.actualStartDate) &&
      isStatusActive(programStatusId as StatusBadgeType)
    ) {
      setIsCompletionDateEnabled(true);
    } else {
      setIsCompletionDateEnabled(false);
    }
  }, [programStatusId, participantDetail?.actualStartDate]);

  const {
    addGoalTitle,
    goalName,
    goalTheme,
    goalDescription,
    notes,
    goalDueDate,
    goalCompletionDate,
    addGoalStatusList,
    goalStatus,
    goalAddedToastMessage,
    goalUpdatedToastMessage,
    editGoalTitle,
    goalCompletionDateValidationMessage,
  } = addGoalSelectionSiteCoreKeys;
  const getLabel = useFetchLabel(addGoalTitle);

  const {
    mutateAsync: addGoal,
    isPending: isAddGoalPending,
    isError: isAddGoalError,
    isSuccess: isAddGoalSuccess,
  } = useAddGoalByProgramId();

  const {
    mutateAsync: updateGoal,
    isPending: isUpdateGoalPending,
    isError: isUpdateGoalError,
    isSuccess: isUpdateGoalSuccess,
  } = useUpdateGoalById();

  const {
    refetchWithParams: fetchGoalDetailsById,
    data: goalDetailsData,
    isFetching: isGoalDetailsPending,
    isError: isGoalDetailsError,
  } = useGetGoalDetailsById();

  const methods = useForm({
    mode: "all",
    defaultValues: {
      [AddGoalFormKey.name]: "",
      [AddGoalFormKey.theme]: "",
      [AddGoalFormKey.description]: "",
      [AddGoalFormKey.dueDate]: "",
      [AddGoalFormKey.completionDate]: "",
      [AddGoalFormKey.completionDateWithoutFormatted]: null,
      [AddGoalFormKey.programStartDateWithoutFormatted]: null,
      [AddGoalFormKey.status]: STATUS_BADGE_TYPE.notYetStarted,
      [AddGoalFormKey.notes]: "",
    },
    resolver: yupResolver(
      AddGoalSchema({
        ...commonValidationMessages,
        goalCompletionDateValidationMessage: getLabel(
          goalCompletionDateValidationMessage,
        ),
        userCountryDateFormat: userCountryDateFormat,
      }),
    ),
  });

  const {
    control,
    getValues,
    formState: { isDirty },
    handleSubmit,
    setValue,
    reset,
  } = methods;

  useEffect(() => {
    if (isEdit && isNotEmpty(goalId)) {
      fetchGoalDetailsById(goalId);
    }
  }, [isEdit, goalId]);

  useEffect(() => {
    setValue(
      AddGoalFormKey.programStartDateWithoutFormatted,
      isNotEmpty(participantDetail?.actualStartDate)
        ? dayjs(participantDetail?.actualStartDate)
        : null,
    );
  }, [participantDetail?.actualStartDate]);

  const getDateValue = (key: keyof typeof AddGoalFormKey) => {
    if (isEmpty(getValues(key))) {
      return null;
    }
    const value = getValues(key) as unknown as string;
    return dayjs(value, userCountryDateFormat);
  };

  const handleCompletionDateValidation = (newValue: dayjs.Dayjs | null) => {
    setValue(AddGoalFormKey.completionDateWithoutFormatted, newValue);
  };

  const handleSaveButtonClick = () => {
    const { name, theme, description, dueDate, completionDate, status, notes } =
      getValues();
    const params: AddGoalParams = {
      name,
      status,
      programId,
      ...getFieldUndefinedForPayloadParams({
        theme,
        description,
        dueDate: dueDate && convertToUTCFormat(dueDate, userCountryDateFormat),
        completionDate:
          completionDate &&
          convertToUTCFormat(completionDate, userCountryDateFormat),
        notes,
      }),
    };
    if (isEdit && goalId) {
      const updateParams: UpdateGoalParams = {
        ...params,
        goalId,
      };
      updateGoal(updateParams);
    } else {
      addGoal(params);
    }
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isFetchingGoals: {
          isVisible:
            isAddGoalPending || isGoalDetailsPending || isUpdateGoalPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      toggleLoader({
        isFetchingGoals: {
          isVisible: false,
        },
      });
    };
  }, [isAddGoalPending, isGoalDetailsPending, isUpdateGoalPending]);

  useEffect(() => {
    if (isAddGoalError || isGoalDetailsError || isUpdateGoalError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater);
    }
  }, [isAddGoalError, isGoalDetailsError, isUpdateGoalError]);

  useEffect(() => {
    if (isAddGoalSuccess) {
      displayToast(getLabel(goalAddedToastMessage));
      onGoalAdded(programId);
      onClose();
    }
  }, [isAddGoalSuccess]);

  useEffect(() => {
    if (isUpdateGoalSuccess) {
      displayToast(getLabel(goalUpdatedToastMessage));
      onGoalAdded(programId);
      onClose();
    }
  }, [isUpdateGoalSuccess]);

  useEffect(() => {
    if (goalDetailsData && isNotEmpty(goalDetailsData)) {
      reset({
        [AddGoalFormKey.dueDate]: isNotEmpty(goalDetailsData.dueDate)
          ? convertToUserFormat(goalDetailsData?.dueDate, userCountryDateFormat)
          : null,
        [AddGoalFormKey.completionDate]: isNotEmpty(
          goalDetailsData.completionDate,
        )
          ? convertToUserFormat(
              goalDetailsData?.completionDate,
              userCountryDateFormat,
            )
          : null,
        [AddGoalFormKey.completionDateWithoutFormatted]: isNotEmpty(
          goalDetailsData.completionDate,
        )
          ? dayjs(goalDetailsData?.completionDate)
          : null,
        [AddGoalFormKey.programStartDateWithoutFormatted]: isNotEmpty(
          participantDetail?.actualStartDate,
        )
          ? dayjs(participantDetail?.actualStartDate)
          : null,
        [AddGoalFormKey.name]: goalDetailsData.name,
        [AddGoalFormKey.theme]: goalDetailsData.theme,
        [AddGoalFormKey.description]: goalDetailsData.description,
        [AddGoalFormKey.status]: goalDetailsData.status,
        [AddGoalFormKey.notes]: goalDetailsData.notes,
      });
    }
  }, [goalDetailsData, participantDetail?.actualStartDate]);

  const handleSecondaryButtonClick = () => {
    if (isDirty) {
      setShowCancel(true);
    } else {
      onClose();
    }
  };

  const closeModalHandler = () => setShowCancel(false);

  const cancelConfirmHandler = () => {
    setShowCancel(false);
    onClose();
  };

  const modalTitle = useMemo(() => {
    if (!isEdit) return fetchComponentTitle(addGoalTitle);

    return goalDetailsData && isNotEmpty(goalDetailsData.name)
      ? `${replaceHashWithValue(getLabel(editGoalTitle), goalDetailsData.name)}`
      : replaceHashWithValue(getLabel(editGoalTitle), "");
  }, [isEdit, goalDetailsData?.name]);

  return (
    <MuiModal
      id={COMPONENT_IDS.ADD_GOAL_MODAL}
      open={open}
      hasLargeContent
      onClose={onClose}
      title={modalTitle}
      primaryButtonTitle={isEdit ? commonLabels.update : commonLabels.save}
      secondaryButtonTitle={commonLabels.cancel}
      onPrimaryButtonClick={handleSubmit(handleSaveButtonClick)}
      onSecondaryButtonClick={handleSecondaryButtonClick}
    >
      <AddGoalWrapper>
        <MuiBox className={"add-goal-modal-box"}>
          <MuiGrid container columnSpacing={2} rowSpacing={isMobile ? 2 : 5}>
            <MuiGrid size={gridSize}>
              <FormController control={control} name={AddGoalFormKey.name}>
                <MuiTextField
                  isMandatory
                  id={`${pageId}-${AddGoalFormKey.name}`}
                  label={getLabel(goalName)}
                  placeholder={commonLabels.inputPlaceHolder}
                  maxLength={MAX_LENGTH_75}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize}>
              <FormController control={control} name={AddGoalFormKey.theme}>
                <MuiTextField
                  id={`${pageId}-${AddGoalFormKey.theme}`}
                  label={getLabel(goalTheme)}
                  placeholder={commonLabels.inputPlaceHolder}
                  maxLength={MAX_LENGTH_75}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
          <MuiBox className="content">
            <MuiBox className="notes-input-box-wrapper">
              <FormController
                control={control}
                name={AddGoalFormKey.description}
              >
                <MuiTextField
                  id={`${pageId}-${AddGoalFormKey.description}`}
                  label={getLabel(goalDescription)}
                  placeholder={commonLabels.inputPlaceHolder}
                  className="notes-input"
                  multiline
                  rows={4}
                  maxLength={MAX_LENGTH_256}
                  data-testid={`${pageId}-${AddGoalFormKey.description}`}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiBox>
          </MuiBox>

          <MuiGrid container columnSpacing={2} rowSpacing={isMobile ? 2 : 5}>
            <MuiGrid size={gridSize}>
              <MuiDateFieldFormController
                control={control}
                name={AddGoalFormKey.dueDate}
                id={`${pageId}-${AddGoalFormKey.dueDate}`}
                value={getDateValue(AddGoalFormKey.dueDate)}
                label={getLabel(goalDueDate)}
                valueFormat={userCountryDateFormat}
                clearable
                className="date-picker"
                format={userCountryDateFormat}
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
              />
            </MuiGrid>
            <MuiGrid size={gridSize}>
              <MuiDateFieldFormController
                control={control}
                name={AddGoalFormKey.completionDate}
                id={`${pageId}-${AddGoalFormKey.completionDate}`}
                value={getDateValue(AddGoalFormKey.completionDate)}
                label={getLabel(goalCompletionDate)}
                valueFormat={userCountryDateFormat}
                disabled={!isCompletionDateEnabled}
                disableFuture
                clearable
                className="date-picker"
                onChange={handleCompletionDateValidation}
                format={userCountryDateFormat}
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
              />
            </MuiGrid>

            <MuiGrid size={gridSize}>
              <FormController control={control} name={AddGoalFormKey.status}>
                <MuiSelect
                  id={`${pageId}-${AddGoalFormKey.status}`}
                  label={getLabel(goalStatus)}
                  disabled={!isEdit}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  options={fetchDropdownValues(addGoalTitle, addGoalStatusList)}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
          <MuiBox className="content">
            <MuiBox className="notes-input-box-wrapper">
              <FormController control={control} name={AddGoalFormKey.notes}>
                <MuiTextField
                  id={`${pageId}-${AddGoalFormKey.notes}`}
                  label={getLabel(notes)}
                  placeholder={commonLabels.inputPlaceHolder}
                  className="notes-input"
                  multiline
                  rows={4}
                  maxLength={MAX_LENGTH_500}
                  data-testid={`${pageId}-${AddGoalFormKey.notes}`}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiBox>
          </MuiBox>
        </MuiBox>
        {showCancel && (
          <WarningConfirmationModal
            show={showCancel}
            onClose={closeModalHandler}
            onPrimaryButtonClick={cancelConfirmHandler}
            secondaryButtonTitle={commonLabels.no}
            primaryButtonTitle={commonLabels.yes}
            labelContent={commonLabels.cancelActionText}
          />
        )}
      </AddGoalWrapper>
    </MuiModal>
  );
};
